import useSWR from "swr";
import useTokenHook from "../../../hooks/context/useTokenHook";
import { shareColumnBackfill } from "../../../utils/requests/shareColumnBackfill";
import type { LinkedBoard } from "../table.data.interface";
import { backfillSourceBoards } from "./shareColumn";
import type { ColumnHolder, ColumnIdentity } from "./shareColumnEligibility";

/**
 * How many connected items each picked board would get values for, from the backend's dry run.
 * Keyed on the picked boards, so ticking a box refetches. A failed preview is not an error the
 * user needs to see: the dialog just leaves the counts out.
 */
const useShareColumnBackfillPreview = (
  identity: ColumnIdentity,
  source: ColumnHolder | undefined,
  holders: ColumnHolder[],
  targets: LinkedBoard[],
) => {
  const { token } = useTokenHook();

  const sourceBoards = source ? backfillSourceBoards(source, holders) : [];
  const targetBoardIds = targets.map((board) => board.boardId).sort();

  const key =
    token && sourceBoards.length > 0 && targetBoardIds.length > 0
      ? `shareColumnBackfillPreview:${identity.type}:${sourceBoards
          .map((board) => `${board.boardId}=${board.columnId}`)
          .join(",")}:${targetBoardIds.join(",")}`
      : null;

  const { data, isLoading } = useSWR(
    key,
    () =>
      shareColumnBackfill(token!, {
        columnTitle: identity.title,
        columnType: identity.type,
        sourceBoards,
        targetBoardIds,
        dryRun: true,
      }),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  return {
    perTargetBoard: data?.perTargetBoard,
    itemCount: data?.itemCount,
    isLoading,
  };
};

export default useShareColumnBackfillPreview;
